import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ApiResponse, CustomerReportData } from "./types/datatype";
import showToastMessage from "../../Toastify/Index";
import { customer_details } from "../../API/api";
import customFetch from "../../API/CustomFetch";
import { formatDate, mainCategoryMapping } from "../../utils/utils";
import CustomerInspectionReport from "./vehicle-inspection-report/CustomerInspectionReport";
import PlainRow from "./common/PlainRow";
import formatBool from "./common/formatBool";
import CustomerConsentOtp from "./common/CustomerConsentOtp";
import Navbar from "../customer-consent/components/Navbar";
import LeadCardSkeleton from "../../layouts/spinners/LeadCardSkeleton";
import RepairCriteriaReport from "./repair-criteria-report/RepairCriteriaReport";

/* -------------------- UI Components -------------------- */

/* -------------------- Reusable Components -------------------- */
const iconMap: Record<string, string> = {
  name: "/icon/user.png",
  phone: "/icon/phone.png",
  city: "/icon/pin.png",
  address: "/icon/pin.png",
  dob: "/icon/date.png",

  vehicleReg: "/icon/vehicle-reg-no.png",
  brand: "/icon/brand.png",
  model: "/icon/modal.png",
  mfgDate: "/icon/date.png",
};
const IconRow = ({
  label,
  value,
  imgUrl,
}: {
  label: string;
  value: any;
  imgUrl: string;
}) => (
  <div className="flex items-start gap-3">
    {/* PNG Icon */}
    <img
      src={imgUrl}
      alt={label}
      className="w-4 h-4 object-contain mt-1 shrink-0"
    />
    {/* Label */}
    <div className="text-black text-sm w-28 shrink-0 md:w-40">{label}</div>
    {/* Value */}
    <div className="bg-white px-3 py-1 rounded text-sm flex-1 min-w-0 break-words text-left md:min-w-[160px] md:text-center shadow-sm">
      {value ?? "N/A"}
    </div>
  </div>
);

// const PlainRow = ({ label, value }: { label: string; value: any }) => (
//   <div className="flex justify-between text-sm">
//     <span className="text-black font-medium">{label}</span>
//     <span className="text-black font-medium">{value ?? "N/A"}</span>
//   </div>
// );

// const formatBool = (val?: number | null) => {
//   if (val === 1) return "Yes";
//   if (val === 0) return "No";
//   return "N/A";
// };

/* -------------------- Component -------------------- */

const CustomerConsentV2Page = () => {
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const [customerReport, setCustomerReport] =
    useState<ApiResponse<CustomerReportData> | null>(null);
  const [invalidVp, setInvalidvp] = useState<number | null>(null);

  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const typeMasterId = query.get("type");
  const vehicleProfileId = query.get("vp");

  const fetchCustomerAndVehicleData = async () => {
    setIsLoading(true);
    try {
      if (!vehicleProfileId || !typeMasterId) return;

      const url = customer_details(vehicleProfileId, typeMasterId);
      const res = await customFetch(url);

      if (res?.SUCCESS) {
        setCustomerReport(res);
      } else {
        setInvalidvp(res?.DATA?.invalidVp);
        showToastMessage(
          false,
          res?.MESSAGE || "Failed to fetch customer data",
        );
      }
    } catch {
      showToastMessage(false, "Error fetching data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerAndVehicleData();
  }, []);

  const data = customerReport?.DATA?.customerData?.[0];

  const customer = data?.CustomerDeets?.[0];
  const vehicle = data?.VehicleDeets?.[0];
  const conversion = data?.ConversionDeets?.[0];
  const req = data?.requirementDeets?.[0];
  const rtoRequirements = data?.rtoRequirements?.[0];
  const specialRequirements = data?.specialRequirements?.[0];
  const loanDetails = data?.loanDeets?.[0];

  // if (customerReport === null)
  //   return (
  //     <div className="text-center text-red-600 font-semibold mt-6">
  //       Unauthorized or Invalid Link...
  //     </div>
  //   );

  return (
    <div>
      <div className="min-h-screen bg-[#F3F3F5] p-3 md:p-6">
        <div>
          <Navbar />
          <h1 className="text-xl font-semibold text-center uppercase mb-2">
            {typeMasterId === "53" && "Vehicle Inspection Report"}
            {typeMasterId === "54" && "Repair Criteria Information"}
          </h1>
          <h2 className="text-gray-700 text-lg font-medium text-center mb-4">
            Customer Consent Form
          </h2>
        </div>
        {isLoading ? (
          <LeadCardSkeleton />
        ) : (
          <>
            {invalidVp === 0 ? (
              <div className="text-center text-red-600 font-semibold mt-6">
                Unauthorized or Invalid Link...
              </div>
            ) : (
              <div className="max-w-5xl mx-auto space-y-6">
                {/* ---------------- Customer Details ---------------- */}
                <div className="bg-[#FFFFFF] rounded-lg p-4 md:p-5 shadow-sm">
                  <h2 className="font-semibold text-black mb-2">
                    Customer Details
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-x-6 md:gap-y-3 bg-[#F4F7F4] p-3 md:p-4">
                    {/* Row 1 */}
                    <IconRow
                      label="Name:"
                      value={customer?.Name}
                      imgUrl="/icon/user.png"
                    />
                    <IconRow
                      label="Mobile No."
                      value={customer?.MobileNo}
                      imgUrl="/icon/phone.png"
                    />
                    {/* Row 2 */}
                    <IconRow
                      label="City:"
                      value={customer?.City}
                      imgUrl="/icon/pin.png"
                    />
                    <IconRow
                      label="PAN:"
                      value={customer?.PAN}
                      imgUrl="/icon/pin.png"
                    />
                    {/* Row 3 */}
                    <IconRow
                      label="DOB:"
                      value={formatDate(customer?.DOB || null)}
                      imgUrl="/icon/date.png"
                    />
                    <IconRow
                      label="Address:"
                      value={customer?.Address}
                      imgUrl="/icon/pin.png"
                    />
                    <div /> {/* empty to balance grid */}
                  </div>
                </div>

                {/* ---------------- Vehicle Details ---------------- */}
                <div className="bg-[#FFFFFF] rounded-lg p-4 md:p-5 shadow-sm">
                  <h2 className="font-semibold text-black mb-2">
                    Vehicle Details
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-6 md:gap-y-3 bg-[#F4F7F4] p-3 md:p-4">
                    {/* Row 1 */}
                    <IconRow
                      label="Vehicle Registration No:"
                      value={vehicle?.VehicleRegNo}
                      imgUrl={iconMap.vehicleReg}
                    />
                    <IconRow
                      label="Model:"
                      value={vehicle?.Model}
                      imgUrl={iconMap.model}
                    />

                    {/* Row 2 */}
                    <IconRow
                      label="Brand:"
                      value={vehicle?.Brand}
                      imgUrl={iconMap.brand}
                    />
                    <IconRow
                      label="Manufacturing Date:"
                      value={vehicle?.MfgYr}
                      imgUrl={iconMap.mfgDate}
                    />
                  </div>
                </div>

                {/* ---------------- Service Requested ---------------- */}
                {(typeMasterId === "53" || typeMasterId === "54") && (
                  <div className="bg-[#FFFFFF] rounded-lg p-4 md:p-5 shadow-sm">
                    <h2 className="font-semibold text-black mb-2">
                      Service Requested
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Price */}
                      <div className="bg-white border rounded-lg">
                        <div className="bg-[#F4F7F4] px-4 py-2 font-medium text-[#00AA13] rounded-t-lg">
                          Price
                        </div>
                        {/* {customer.conversionTypeId != null
                  ? (mainCategoryMapping[customer.conversionTypeId] ?? "N/A")
                  : "N/A"} */}
                        {/* 
                  conversion?.converstionType !== null &&
                    conversion?.converstionType !== undefined && */}

                        <div className="p-4 space-y-3">
                          {
                            <PlainRow
                              label="Conversion Type"
                              // value={conversion?.converstionType}
                              value={
                                mainCategoryMapping[
                                  Number(conversion?.conversionTypeId)
                                ]
                              }
                            />
                          }

                          <PlainRow
                            label="Conversion Price"
                            value={
                              conversion?.conversionPrice
                                ? `₹ ${conversion.conversionPrice}`
                                : "N/A"
                            }
                          />
                          <PlainRow
                            label="Booked Amount"
                            value={
                              conversion?.bookedAmount
                                ? `₹ ${conversion.bookedAmount}`
                                : "N/A"
                            }
                          />
                        </div>
                      </div>

                      {/* Requirements */}
                      <div className="bg-white border rounded-lg">
                        <div className="bg-[#F4F7F4] px-4 py-2 font-medium text-[#00AA13] rounded-t-lg">
                          Requirements
                        </div>

                        <div className="p-4 space-y-3">
                          <PlainRow
                            label="Loan Required"
                            value={formatBool(req?.loanRequired)}
                          />
                          <PlainRow
                            label="Insurance Required"
                            value={formatBool(req?.insuranceRequired)}
                          />
                          <PlainRow
                            label="RSA Required"
                            value={formatBool(req?.rsaRequired)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Loan */}
                    {req?.loanRequired === 1 && (
                      <div className="mt-6">
                        <h3 className="text-sm font-medium mb-2">
                          Loan (if loan selected)
                        </h3>

                        <div className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
                          <PlainRow
                            label="Loan Status"
                            value={formatBool(loanDetails?.loanStatus)}
                            // value={formatBool(loanDetails?.loanStatus)}
                          />
                          <PlainRow
                            label="GT Loan"
                            value={formatBool(loanDetails?.gtLoan)}
                          />
                          <PlainRow
                            label="Indofast Loan"
                            value={formatBool(loanDetails?.indofast)}
                          />
                          <PlainRow
                            label="Perpetuity Loan"
                            value={formatBool(loanDetails?.perpLoan)}
                          />
                        </div>
                      </div>
                    )}

                    {/* RTO */}
                    <div className="mt-6">
                      <h3 className="text-sm font-medium mb-2">
                        RTO Requirements
                      </h3>

                      <div className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
                        <PlainRow
                          label="RTO Interested"
                          value={formatBool(rtoRequirements?.rtoInterested)}
                        />
                        <PlainRow
                          label="FC Valid"
                          value={formatBool(rtoRequirements?.fcValid)}
                        />
                        {rtoRequirements?.fcValid === 1 && (
                          <PlainRow
                            label="FC Expiry Date"
                            value={formatDate(rtoRequirements?.fcExpiry)}
                          />
                        )}

                        {
                          <>
                            <PlainRow
                              label="RTO/ Type Conversion Required"
                              value={formatBool(
                                rtoRequirements?.typeConversionRequired,
                              )}
                            />
                            <PlainRow
                              label="Fitness Extension Required"
                              value={formatBool(
                                rtoRequirements?.fitnessExtRequired,
                              )}
                            />
                            <PlainRow
                              label="Duplicate RC Required"
                              value={formatBool(rtoRequirements?.dupRcRequired)}
                            />
                            <PlainRow
                              label="Change in Ownership Required"
                              value={formatBool(
                                rtoRequirements?.ownerChngRequired,
                              )}
                            />
                            <PlainRow
                              label="Change In Address Required"
                              value={formatBool(
                                rtoRequirements?.addrChngRequired,
                              )}
                            />
                          </>
                        }
                      </div>
                    </div>

                    {/* Special Request*/}
                    {specialRequirements?.paintingRequired === 1 ? (
                      <div className="mt-6">
                        <h3 className="text-sm font-medium mb-2">
                          Special Requests
                        </h3>

                        <div className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
                          {specialRequirements?.paintingRequired === 1 && (
                            <PlainRow
                              label="Painting Required"
                              value={formatBool(
                                specialRequirements?.paintingRequired,
                              )}
                            />
                          )}

                          {specialRequirements?.paintingRequired === 1 && (
                            <PlainRow
                              label="Painting Color"
                              value={specialRequirements?.paintColor}
                              color="bg-red-500 px-4 py-2 text-white rounded-lg max-w-md"
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}

                    {/* Parts Returned */}
                    {specialRequirements?.battReturn === 1 ||
                    specialRequirements?.engReturn === 1 ||
                    specialRequirements?.tyreReturn === 1 ||
                    specialRequirements?.wheelReturn === 1 ? (
                      <div className="mt-6">
                        <h3 className="text-sm font-medium mb-2">
                          Parts Returned
                        </h3>

                        <div className="bg-white border rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2">
                          {specialRequirements?.battReturn === 1 && (
                            <PlainRow
                              label="Lead Acid Battery Return:"
                              value={formatBool(
                                specialRequirements?.battReturn,
                              )}
                            />
                          )}

                          {specialRequirements?.engReturn === 1 && (
                            <PlainRow
                              label="Engine Return"
                              value={formatBool(specialRequirements?.engReturn)}
                            />
                          )}

                          {specialRequirements?.tyreReturn === 1 && (
                            <PlainRow
                              label="Tyre Return"
                              value={formatBool(
                                specialRequirements?.tyreReturn,
                              )}
                            />
                          )}

                          {specialRequirements?.wheelReturn === 1 && (
                            <PlainRow
                              label="Wheel Return"
                              value={formatBool(
                                specialRequirements?.wheelReturn,
                              )}
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                )}

                {typeMasterId === "53" && (
                  <CustomerInspectionReport
                    vehicleProfileId={vehicleProfileId!}
                  />
                )}
                {typeMasterId === "54" && (
                  <RepairCriteriaReport vehicleProfileId={vehicleProfileId!} />
                )}
                {customerReport !== null &&
                  vehicleProfileId !== null &&
                  ["53", "54"].includes(String(typeMasterId)) && (
                    <CustomerConsentOtp
                      typeMasterId={String(typeMasterId)}
                      vehicleProfileId={vehicleProfileId}
                      customerVerifiedOTP={customer?.CustomerVerified}
                    />
                  )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerConsentV2Page;
